import * as React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Menu,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import dayjs from "dayjs";
import { GET_FESTIVAL_SETTINGS } from "./graphql";
import { GET_EDITIONS } from "../editions/graphql";
import { GET_EDITION } from "../edition/edit/graphql";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";
import { UpdateAppButton } from "../links/updateAppButton";

export const SettingsUpdater = () => {
  const { data } = useQuery(GET_FESTIVAL_SETTINGS);
  const { data: dataEditions } = useQuery(GET_EDITIONS);
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  );

  const editionId = data?.settings?.[0]?.editionId;
  const edition = dataEditions?.festivalEditions.find(
    (edition) => edition.id === editionId
  );
  const { data: editionDetailData } = useQuery(GET_EDITION, {
    variables: { festivalId: editionId ?? "" },
    skip: !editionId,
    fetchPolicy: "cache-first",
  });

  const activeEdition = editionDetailData?.festivalEdition ?? edition;
  const availabilityDate = editionDetailData?.festivalEdition?.availabilityDate;
  const availabilityLabel = React.useMemo(() => {
    if (!availabilityDate) return "Not set";
    const parsed = dayjs(availabilityDate);
    if (!parsed.isValid()) return availabilityDate;
    return parsed.format("YYYY/MM/DD HH:mm");
  }, [availabilityDate]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  React.useEffect(() => {
    if (!isXS && menuAnchorEl) {
      setMenuAnchorEl(null);
    }
  }, [isXS, menuAnchorEl]);

  if (!activeEdition) return null;

  const ActionGroup = ({ stacked = false }: { stacked?: boolean }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "flex-start" : "center",
        gap: stacked ? 2 : 3,
        minWidth: stacked ? 240 : "auto",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 300,
          whiteSpace: stacked ? "normal" : "nowrap",
        }}
      >
        Live in app:{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {activeEdition.name}
        </Box>
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 300,
          whiteSpace: stacked ? "normal" : "nowrap",
        }}
      >
        Set times visible on:{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {availabilityLabel}
        </Box>
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : "row",
          gap: stacked ? 1.5 : 3,
          width: stacked ? "100%" : "auto",
        }}
      >
        {/* @ts-ignore */}
        {/* <UpdateAppButton edition={edition} /> */}
        <UpdateMiscConfigButton />
      </Box>
    </Box>
  );

  if (isXS) {
    const menuOpen = Boolean(menuAnchorEl);
    const menuId = "settings-updater-menu";
    return (
      <>
        <Button
          color="inherit"
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={handleMenuOpen}
          aria-haspopup="true"
          aria-controls={menuOpen ? menuId : undefined}
          sx={{ whiteSpace: "nowrap" }}
        >
          Updates
        </Button>
        <Menu
          id={menuId}
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{
            sx: { p: 1 },
          }}
        >
          <ActionGroup stacked />
        </Menu>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        pl: 1,
        minWidth: 0,
      }}
    >
      <ActionGroup />
    </Box>
  );
};
